'use client';

import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm'

// Visual cue placeholder component
const VisualCuePlaceholder: React.FC<{ alt: string; src: string }> = ({ alt, src }) => (
  <div className="my-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
    <div className="text-yellow-700 mb-2">📊 Visual Element</div>
    <p className="text-sm text-gray-600">{alt}</p>
    <p className="text-xs text-gray-500 mt-1">Visual ID: {src}</p>
  </div>
);

// Shared markdown renderer with consistent Twygs styling
export const MarkdownRenderer: React.FC<{ 
  content: string; 
  className?: string;
}> = ({ content, className = "prose prose-sm max-w-none" }) => {
  return (
    <div className={className}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        skipHtml={false}
        components={{
          // Only override specific elements, let HTML with classes pass through
          img: ({ alt, src, ...props }) => {
            if (src?.startsWith('visual-') || src?.includes('visual')) {
              return <VisualCuePlaceholder alt={alt || ''} src={src || ''} />;
            }
            return (
              <img 
                src={src} 
                alt={alt} 
                className="w-full h-auto rounded-lg my-4"
                {...props}
              />
            );
          },
          // Let other elements render with their original classes
          // Only provide fallback styling for plain markdown elements
          h1: ({ children, className, ...props }) => {
            if (className) {
              return <h1 className={className} {...props}>{children}</h1>;
            }
            return <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6" {...props}>{children}</h1>;
          },
          h2: ({ children, className, ...props }) => {
            if (className) {
              return <h2 className={className} {...props}>{children}</h2>;
            }
            return <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-6" {...props}>{children}</h2>;
          },
          h3: ({ children, className, ...props }) => {
            if (className) {
              return <h3 className={className} {...props}>{children}</h3>;
            }
            return <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4" {...props}>{children}</h3>;
          },
          p: ({ children, className, ...props }) => {
            if (className) {
              return <p className={className} {...props}>{children}</p>;
            }
            return <p className="mb-3 text-gray-700 leading-relaxed" {...props}>{children}</p>;
          },
          div: ({ children, className, ...props }) => {
            // Let div elements pass through with their classes
            return <div className={className} {...props}>{children}</div>;
          },
          strong: ({ children, className, ...props }) => {
            if (className) {
              return <strong className={className} {...props}>{children}</strong>;
            }
            return <strong className="font-semibold text-gray-900" {...props}>{children}</strong>;
          },
          em: ({ children, className, ...props }) => {
            if (className) {
              return <em className={className} {...props}>{children}</em>;
            }
            return <em className="italic" {...props}>{children}</em>;
          },
          // Custom table styling (for plain markdown tables)
          table: ({ children, className, ...props }) => {
            if (className) {
              return <table className={className} {...props}>{children}</table>;
            }
            return (
              <div className="overflow-x-auto my-4">
                <table className="min-w-full border border-gray-200 rounded-lg" {...props}>
                  {children}
                </table>
              </div>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
